import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { FlexContainer } from '../../core/components/FlexContainer';
import { Font, FontWeight, TextElement } from '../../core/components/TextElement';
import { ErrorCard } from '../../core/components/ErrorCard';
import { ScreenSize, useScreenSize } from '../../core/util/MediaQueries';
import { matchesAny } from '../../core/util/RegexUtils';
import { TextInput } from '../../core/components/TextArea';
import { ChatWindowOuterContainer } from '../../core/components/ChatWindowOuterContainer';
import { useCallback, useMemo } from 'react';
import { useGameState } from '../../../game/contact3d/replicatedObjects/GameState';
import { useClue } from '../../../game/contact3d/replicatedObjects/Clue';
export var Contact3DChatInputState;
(function (Contact3DChatInputState) {
    Contact3DChatInputState["Answer"] = "Answer";
    Contact3DChatInputState["ClueHint"] = "ClueHint";
    Contact3DChatInputState["ClueSecret"] = "ClueSecret";
    Contact3DChatInputState["SecretWord"] = "SecretWord";
    Contact3DChatInputState["UpdateHint"] = "UpdateHint";
    Contact3DChatInputState["Waiting"] = "Waiting";
})(Contact3DChatInputState || (Contact3DChatInputState = {}));
let scrollPos = 0;
export const ChatWindow = (props) => {
    const { inputEnabled, state, enabledCharacterTypes, clueSecret, currentError, chatInputCallbacks, handleTextAreaFocus, handleTextAreaBlur, isCondensed = false, fixed, value, setValue, errorOnClose, } = props;
    const testKey = useCallback((key) => matchesAny(key, enabledCharacterTypes), [enabledCharacterTypes]);
    const gameState = useGameState();
    const clue = useClue();
    const handleKeyDown = useCallback((e) => {
        if (e.key === 'Tab') {
            e.preventDefault();
            chatInputCallbacks.onTab();
            return;
        }
        if (e.key === 'Enter') {
            e.preventDefault();
            chatInputCallbacks.onReturn();
            return;
        }
    }, [chatInputCallbacks, inputEnabled]);
    const onChange = useCallback((e) => {
        if (!inputEnabled || state === Contact3DChatInputState.Waiting) {
            return;
        }
        setValue(e.target.value.split('').filter((char) => testKey(char)).join('').toLowerCase());
    }, [setValue, testKey]);
    const oldestClue = useMemo(() => (clue === null || clue === void 0 ? void 0 : clue.hints) ? clue.hints[0] : '', [clue]);
    const stateText = useMemo(() => getStateText(state), [state]);
    const placeholderText = useMemo(() => getPlaceholderText(state), [state]);
    const helperText = useMemo(() => getHelperText(state, oldestClue, clueSecret, gameState.currentSecretWordLetters), [state, oldestClue, clueSecret, gameState]);
    const screenSize = useScreenSize();
    const outerPadding = (screenSize === ScreenSize.Large && !props.fixed) ? undefined : `${'6px'} ${'20px'}`;
    const outerGap = (screenSize === ScreenSize.Large && !props.fixed) ? '12px' : '6px';
    const handleTextareaFocus = useCallback(() => {
        if (screenSize !== ScreenSize.Large) {
            scrollPos = window.scrollY;
        }
        if (handleTextAreaFocus !== undefined) {
            handleTextAreaFocus();
        }
    }, [screenSize]);
    const handleTextAreaBlurInternal = useCallback(() => {
        if (screenSize !== ScreenSize.Large) {
            window.scrollTo({
                top: scrollPos,
            });
        }
        if (handleTextAreaBlur !== undefined) {
            handleTextAreaBlur();
        }
    }, [scrollPos, screenSize]);
    const helperTextColor = fixed ? '#FFFFFF' : screenSize === ScreenSize.Large ? '#AEAEAE' : '#696969';
    const titleColor = fixed ? '#FFFFFF' : '#000000';
    return (_jsx(ChatWindowOuterContainer, { isCondensed: isCondensed, fixed: fixed !== null && fixed !== void 0 ? fixed : false, children: _jsxs(FlexContainer, { padding: outerPadding, gap: outerGap, children: [_jsxs(_Fragment, { children: [currentError !== undefined && _jsx(ErrorCard, { errorText: currentError, onClose: errorOnClose, fixed: fixed }), currentError === undefined && _jsx(TextElement, { font: Font.Inter, fontSize: 16, text: stateText, color1: titleColor, fontWeight: FontWeight.Medium, centered: true })] }), _jsx(TextInput, { helperText: helperText, helperTextColor: helperTextColor, onKeyDown: handleKeyDown, onChange: onChange, onFocus: handleTextareaFocus, onBlur: handleTextAreaBlurInternal, value: value, disabled: state === Contact3DChatInputState.Waiting, placeholder: placeholderText, type: 'area', style: {
                        width: '100%',
                        height: '5.2rem',
                    } })] }) }));
};
const STATE_LABELS = {
    [Contact3DChatInputState.SecretWord]: 'Mastermind',
    [Contact3DChatInputState.ClueHint]: 'Submit Hint',
    [Contact3DChatInputState.ClueSecret]: 'Submit Clue',
    [Contact3DChatInputState.Answer]: 'Guess Answer',
    [Contact3DChatInputState.UpdateHint]: 'Add Hint',
    [Contact3DChatInputState.Waiting]: 'Waiting',
};
const STATE_PLACEHOLDERS = {
    [Contact3DChatInputState.SecretWord]: 'Submit a secret word',
    [Contact3DChatInputState.ClueHint]: 'Submit a hint for your word',
    [Contact3DChatInputState.ClueSecret]: 'Submit a word for other players to guess',
    [Contact3DChatInputState.Answer]: 'Guess an answer',
    [Contact3DChatInputState.UpdateHint]: 'Add another hint',
    [Contact3DChatInputState.Waiting]: '',
};
export function getStateText(state) {
    return STATE_LABELS[state];
}
export function getPlaceholderText(state) {
    return STATE_PLACEHOLDERS[state];
}
export function getHelperText(state, oldestClue, clueSecret, currentSecretWordLetters) {
    switch (state) {
        case Contact3DChatInputState.Answer:
        default:
            return `Guess an answer for '${oldestClue.toLowerCase()}'`;
        case Contact3DChatInputState.ClueHint:
            return `Submit a hint for your word '${clueSecret.toLowerCase()}'`;
        case Contact3DChatInputState.ClueSecret:
            return `Submit a word starting with '${currentSecretWordLetters}'`;
        case Contact3DChatInputState.SecretWord:
            return 'Choose the secret word for this round of the game';
        case Contact3DChatInputState.UpdateHint:
            return `Add another hint for your word '${clueSecret.toLowerCase()}'`;
        case Contact3DChatInputState.Waiting:
            return `Wait for somebody to submit a word`;
    }
}
//# sourceMappingURL=ChatWindow.js.map